import React, { useCallback, useState } from 'react';
import { Button, Col, Form, FormProps, Row, Modal } from 'react-bootstrap';
import ReactDatePicker from 'react-datepicker';
import { Controller, useForm } from 'react-hook-form';
import { AiOutlineContainer, AiOutlinePrinter } from 'react-icons/ai';

import cx from 'classnames';
import moment from 'moment';
import { Validation } from 'utils/validationForm';

import Card from 'components/Card/Card';
import Circle from 'components/Circle/Circle';
import TextareaWithCounter from 'components/TextareaWithCounter/TextareaWithCounter';
import { DeliveryOptions } from 'models/deliveryOptions';

import s from './HowToSendYourGiftForm.module.scss';

export interface IHowToSendYourGiftFormValues {
  recipient_first_name?: string;
  recipient_last_name?: string;
  recipient_email?: string;
  recipient_message?: string;
  delivery_dt?: string | Date;
  delivery_now?: boolean;
  delivery_option?: DeliveryOptions;
  recipient_custom_to?: string;
  recipient_custom_from?: string;
}
interface IHowToSendYourGiftFormProps extends Omit<FormProps, 'onSubmit'> {
  previewImageURL?: string;
  defaultValues?: IHowToSendYourGiftFormValues;
  loading?: boolean;
  onSubmit?: (
    values: IHowToSendYourGiftFormValues,
    asAGuest: boolean,
    createNewAccountTabActive: boolean,
  ) => any;
}
const DELIVERY_OPTIONS = [
  {
    icon: <AiOutlineContainer size={20} className="mr-1" />,
    value: DeliveryOptions.ECard,
    label: 'Ecard personalised and emailed to a recipient',
  },
  {
    icon: <AiOutlinePrinter size={20} className="mr-1" />,
    value: DeliveryOptions.PDF,
    label: 'PDF download to print card at home',
  },
];
const HowToSendYourGiftForm: React.FC<IHowToSendYourGiftFormProps> = ({
  previewImageURL,
  onSubmit = () => {},
  defaultValues = {
    delivery_dt: moment().hours(9).minutes(0).toDate(),
  },
  loading,
  ...props
}) => {
  const [previewValues, setPreviewValues] = useState<
    IHowToSendYourGiftFormValues | undefined
  >();
  const { register, control, handleSubmit, errors, watch, getValues } = useForm(
    {
      defaultValues,
    },
  );
  const deliveryOption = watch('delivery_option') as
    | DeliveryOptions
    | undefined;

  const [deliveryNow, setDeliveryNow] = useState(true);
  const [PDFWithDetail, setPDFWithDetail] = useState(true);

  const getClassAndNameForFormControl = useCallback(
    (name: string) => {
      return {
        name,
        className: cx('form-control form-control-bordered', {
          'is-invalid': (errors as any)?.[name],
        }),
      };
    },
    [errors],
  );

  const handlePreview = useCallback(() => {
    return setPreviewValues(getValues());
  }, [getValues]);

  const renderPreviewButton = useCallback(() => {
    return previewImageURL ? (
      <div className="d-flex justify-content-center">
        <Button onClick={handlePreview}>Preview</Button>
      </div>
    ) : (
      <></>
    );
  }, [handlePreview, previewImageURL]);

  const renderStepLabel = useCallback((step: number, label: string) => {
    return (
      <div className="d-flex align-items-center mb-2">
        <Circle
          className="text-white text-center bg-primary mr-2 font-weight-bold no-shadow caption"
          size="xxs"
        >
          {step}
        </Circle>
        <h5 className="text-primary d-flex">{label}</h5>
      </div>
    );
  }, []);

  const renderFormRecipientDetails = useCallback(
    ({ withEmail } = {}) => {
      return (
        <div className="mb-4">
          {renderStepLabel(1, 'Recipient details')}
          <Form.Group>
            <Row>
              <Col className="mb-3 mb-md-0" md={6}>
                <Form.Group className="mb-0">
                  <Form.Control
                    {...getClassAndNameForFormControl('recipient_first_name')}
                    placeholder="Recipient's first name"
                    ref={register({ required: Validation.required })}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-0">
                  <Form.Control
                    {...getClassAndNameForFormControl('recipient_last_name')}
                    placeholder="Recipient's last name"
                    ref={register({ required: Validation.required })}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Form.Group>
          {withEmail && (
            <Form.Group>
              <Form.Control
                type="email"
                placeholder="Recipient's Email"
                {...getClassAndNameForFormControl('recipient_email')}
                ref={register({
                  required: Validation.required,
                  pattern: Validation.email,
                })}
              />
            </Form.Group>
          )}
        </div>
      );
    },
    [getClassAndNameForFormControl, register, renderStepLabel],
  );

  const renderFormCustomMessage = useCallback(() => {
    return (
      <div className="mb-4">
        {renderStepLabel(2, 'Write a message on the card')}
        <Form.Group>
          <Row>
            <Col md="auto">
              <Form.Label>To:</Form.Label>
            </Col>
            <Col md="auto" className="pl-md-0 flex-grow-1">
              <Form.Control
                placeholder="Who you’re writing the card to*"
                {...getClassAndNameForFormControl('recipient_custom_to')}
                ref={register({ required: Validation.required })}
              />
            </Col>
          </Row>
        </Form.Group>
        <Form.Group>
          <Row>
            <Col md="auto">
              <Form.Label>Message:</Form.Label>
            </Col>
            <Col md="auto" className="pl-md-0 flex-grow-1">
              <Controller
                control={control}
                as={TextareaWithCounter}
                id="recipient_message"
                name="recipient_message"
                rules={{ required: true, maxLength: 150 }}
                maxLength={150}
                formControlProps={{
                  className: cx('form-control form-control-bordered', {
                    'is-invalid': errors.recipient_message,
                  }),
                }}
              />
            </Col>
          </Row>
        </Form.Group>
        <Form.Group>
          <Row>
            <Col md="auto">
              <Form.Label>From:</Form.Label>
            </Col>
            <Col md="auto" className="pl-md-0 flex-grow-1">
              <Form.Control
                placeholder="Who you’re signing the card from*"
                {...getClassAndNameForFormControl('recipient_custom_from')}
                ref={register({ required: Validation.required })}
              />
            </Col>
          </Row>
        </Form.Group>
      </div>
    );
  }, [
    control,
    errors.recipient_message,
    getClassAndNameForFormControl,
    register,
    renderStepLabel,
  ]);

  const displaySubForm = useCallback(() => {
    switch (deliveryOption) {
      case DeliveryOptions.ECard:
        return (
          <>
            {renderFormRecipientDetails({ withEmail: true })}
            {renderFormCustomMessage()}
            <div className="mb-4">
              {renderStepLabel(3, 'When should we send your gift?')}
              <Form.Group>
                <Row>
                  <Col xs="auto">
                    <Form.Check
                      value={1}
                      type="radio"
                      id="delivery_now-0"
                      name="delivery_now"
                      checked={deliveryNow}
                      onClick={() => setDeliveryNow(true)}
                      label={<span className="ml-2">Send now</span>}
                    />
                  </Col>
                  <Col xs="auto">
                    <span>or</span>
                  </Col>
                  <Col xs="auto">
                    <Form.Check
                      value={0}
                      type="radio"
                      id="delivery_now-1"
                      name="delivery_now"
                      checked={!deliveryNow}
                      onClick={() => setDeliveryNow(false)}
                      label={<span className="ml-2">Send later</span>}
                    />
                  </Col>
                </Row>
              </Form.Group>
              {!deliveryNow && (
                <Form.Group>
                  <Controller
                    control={control}
                    name="delivery_dt"
                    rules={{ required: true }}
                    render={({ onChange, value }) => (
                      <ReactDatePicker
                        showTimeSelect
                        className={cx('form-control form-control-bordered', {
                          'is-invalid': errors.delivery_dt,
                        })}
                        minDate={new Date()}
                        dateFormat="dd/MM/yyyy hh:mm"
                        placeholderText="DD/MM/YYYY"
                        selectsStart
                        selected={value ? moment(value).toDate() : null}
                        onChange={onChange}
                      />
                    )}
                  />
                </Form.Group>
              )}
            </div>
            {renderPreviewButton()}
          </>
        );
      case DeliveryOptions.PDF:
        return (
          <>
            <Form.Group>
              <Row>
                <Col xs="auto">
                  <Form.Check
                    value={1}
                    type="radio"
                    id="pdf_with_detail-0"
                    name="pdf_with_detail"
                    checked={PDFWithDetail}
                    onClick={() => setPDFWithDetail(true)}
                    label={<span className="ml-2">Enter details</span>}
                  />
                </Col>
                <Col xs="auto">
                  <span>or</span>
                </Col>
                <Col xs="auto">
                  <Form.Check
                    value={0}
                    type="radio"
                    id="pdf_with_detail-1"
                    name="pdf_with_detail"
                    checked={!PDFWithDetail}
                    onClick={() => setPDFWithDetail(false)}
                    label={<span className="ml-2">Print a blank card</span>}
                  />
                </Col>
              </Row>
            </Form.Group>
            {PDFWithDetail && (
              <>
                {renderFormRecipientDetails()}
                {renderFormCustomMessage()}
                {renderPreviewButton()}
              </>
            )}
          </>
        );
      default:
        return <></>;
    }
  }, [
    PDFWithDetail,
    control,
    deliveryNow,
    deliveryOption,
    errors.delivery_dt,
    renderFormCustomMessage,
    renderFormRecipientDetails,
    renderPreviewButton,
    renderStepLabel,
  ]);

  const beforeHandleSubmit = useCallback(
    (value: IHowToSendYourGiftFormValues) => {
      onSubmit(
        {
          ...value,
          delivery_now:
            deliveryOption === DeliveryOptions.ECard ? deliveryNow : undefined,
        },
        false,
        false,
      );
    },
    [deliveryNow, deliveryOption, onSubmit],
  );

  return (
    <Form onSubmit={handleSubmit(beforeHandleSubmit)} {...props}>
      <Card loading={loading} className="p-0 no-shadow align-items-start">
        {DELIVERY_OPTIONS.map(({ icon, label, value }) => (
          <Form.Group className="w-100" key={value}>
            <Form.Check
              value={value}
              type="radio"
              id={`delivery_option-${value}`}
              name="delivery_option"
              ref={register({ required: Validation.required })}
              label={
                <div className="ml-3 cursor-pointer">
                  {icon}
                  <span className=" font-weight-bold">{label}</span>
                </div>
              }
            />
            {value === deliveryOption && (
              <div className="mt-2 pl-4">{displaySubForm()}</div>
            )}
          </Form.Group>
        ))}
        <p>
          *Note: You will need to process a separate transaction for each gift
          card you purchase.
        </p>
        <Button type="submit" variant="danger" disabled={loading}>
          Purchase Card
        </Button>
      </Card>
      {previewImageURL && (
        <Modal
          show={!!previewValues}
          size="xl"
          onHide={() => setPreviewValues(undefined)}
        >
          <Modal.Header closeButton>Preview</Modal.Header>
          <Modal.Body className="position-relative">
            <div className="position-absolute z-100 w-100 top-0 left-0 right-0 bottom-0">
              <h3
                className={cx('text-center text-primary', s.recipientCustomTo)}
              >
                {previewValues?.recipient_custom_to}
              </h3>
              <h4
                className={cx('text-center text-primary', s.recipientMessage)}
              >
                <div className="mb-4 text-break">
                  {previewValues?.recipient_message}
                </div>
                {previewValues?.recipient_custom_from}
              </h4>
            </div>
            <img
              src={previewImageURL}
              alt="Preview"
              className="d-block w-100"
            />
          </Modal.Body>
        </Modal>
      )}
    </Form>
  );
};

export default HowToSendYourGiftForm;
